import { ComponentProps, useMemo } from 'react';

import keyBy from 'lodash/keyBy';
import map from 'lodash/map';

import { useSchedulePeriodsV2 } from '@sbv-clients/data-access-api';
import { FormSelectField } from '@sbv-clients/ui-forms';
import { styled, Typography } from '@sbv-clients/ui-shared';
import { formatDateTimeString } from '@sbv-clients/util-misc';

const ToText = styled(Typography)`
  color: ${(props) => props.theme.text.placeholder};
`;

type PeriodsFieldProps = ComponentProps<typeof FormSelectField> & {
  facilityId: number;
  departmentId: number;
};

const usePayPeriodStartAndEndDate = (
  facilityId: number,
  departmentId: number,
  schedulePeriodId: number
) => {
  const { data: periods = [] } = useSchedulePeriodsV2(facilityId, departmentId);
  const getStartAndEndDate = () => {
    const schedulePeriodMap = keyBy(periods, 'schedulePeriodId');
    const schedulePeriod = schedulePeriodMap[schedulePeriodId];
    return {
      startDate: schedulePeriod.startDate,
      endDate: schedulePeriod.endDate,
    };
  };

  return {
    getStartAndEndDate,
  };
};

const SchedulePeriodFormField = ({
  facilityId,
  departmentId,
  ...props
}: PeriodsFieldProps) => {
  const { data: periods = [] } = useSchedulePeriodsV2(facilityId, departmentId);
  const options = useMemo(() => {
    return map(periods, (period) => ({
      id: period.schedulePeriodId as number,
      value: period.schedulePeriodId as number,
      label: (
        <>
          <Typography display="inline" mr={1}>
            {formatDateTimeString(period.startDate, 'MM-dd-yyyy')}
          </Typography>
          <ToText display="inline" mr={1}>
            to
          </ToText>
          <Typography display="inline">
            {formatDateTimeString(period.endDate, 'MM-dd-yyyy')}
          </Typography>
        </>
      ),
    }));
  }, [periods]);

  return (
    <FormSelectField
      fullWidth
      size="small"
      {...props}
      label="Schedule Period"
      options={options}
    />
  );
};

SchedulePeriodFormField.usePayPeriodStartAndEndDate =
  usePayPeriodStartAndEndDate;

export { SchedulePeriodFormField };
